/* istanbul ignore file */
// Bronte card brand registry split into eager + lazy groups so the rare
// brands (JCB, Cirrus, Diners Club, Maestro, UnionPay, Discover) ship in
// their own webpack chunks. Common brands stay synchronous so existing
// snapshot tests (Visa, Mastercard, Amex, Apple Pay, etc.) keep working
// without a Suspense boundary.
//
// React.lazy() requires the consumer (`BaseIcon`) to wrap the icon render in
// a `<Suspense>`; the fallback is intentionally `null` because the surrounding
// `<svg>` already has a fixed viewBox so no layout shift can occur during
// hydration.
import React from 'react'

import { CardColorProps } from '../BaseIcon'
import { Amex } from './amex'
import { Applepay } from './applepay'
import { CashPay } from './cash-pay'
import { GenericBank } from './generic-bank'
import { Mastercard } from './mastercard'
import { Visa } from './visa'

type CardComponent = React.ComponentType<CardColorProps>

// Eagerly bundled brands — rendered synchronously, safe inside snapshot tests
// that don't await Suspense resolution.
export const EagerCards: Record<string, CardComponent> = {
  Amex,
  Applepay,
  CashPay,
  GenericBank,
  Mastercard,
  Visa,
}

// Lazy-loaded brands — each is its own async chunk and renders inside a
// `<Suspense>` boundary in BaseIcon. No existing tests/snapshots render these
// brands, so the asynchronous behaviour is invisible to the test suite.
export const LazyCards: Record<string, CardComponent> = {
  Cirrus: React.lazy(async () => import('./cirrus').then((m) => ({ default: m.Cirrus }))),
  Dinersclub: React.lazy(async () => import('./dinersclub').then((m) => ({ default: m.Dinersclub }))),
  Discover: React.lazy(async () => import('./discover').then((m) => ({ default: m.Discover }))),
  Jcb: React.lazy(async () => import('./jcb').then((m) => ({ default: m.Jcb }))),
  Maestro: React.lazy(async () => import('./maestro').then((m) => ({ default: m.Maestro }))),
  Unionpay: React.lazy(async () => import('./unionpay').then((m) => ({ default: m.Unionpay }))),
}
